/**
 * RTK Query endpoints — Polymorphic entity tags
 *
 * `useGetTagsForEntityQuery({ taggableType, taggableId })` and
 * `useSetTagsForEntityMutation()` work for any of the four taggable
 * kinds (Resource, Flow, Item, Control). Routes are computed from
 * `taggableType` so the hook stays generic; the backend uses one factory
 * (`endpoints/su/_taggable`) mounted under each entity's router.
 *
 * `invalidatesTags` returns both the per-entity tag entry and the
 * corresponding entity type's LIST tag so any list page that displays
 * tags as a column refetches automatically.
 */

import { baseApi } from './baseApi';

export const TAGGABLE_TYPES = Object.freeze({
  RESOURCE: 'Resource',
  FLOW: 'Flow',
  ITEM: 'Item',
  CONTROL: 'Control',
});

const ENTITY_ROUTES = Object.freeze({
  Resource: '/su/resource',
  Flow: '/su/flows',
  Item: '/su/items',
  Control: '/su/control',
});

function routeFor(taggableType, taggableId) {
  const base = ENTITY_ROUTES[taggableType];
  if (!base) {
    throw new Error(`Unknown taggable type: ${taggableType}`);
  }
  return `${base}/${taggableId}/tags`;
}

const taggableApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET /su/<entity>/:id/tags
    getTagsForEntity: builder.query({
      query: ({ taggableType, taggableId }) => ({
        url: routeFor(taggableType, taggableId),
        method: 'GET',
      }),
      providesTags: (_r, _e, { taggableType, taggableId }) => [
        { type: 'EntityTags', id: `${taggableType}:${taggableId}` },
      ],
    }),

    // PUT /su/<entity>/:id/tags — replaces all tags atomically
    setTagsForEntity: builder.mutation({
      query: ({ taggableType, taggableId, tagIds }) => ({
        url: routeFor(taggableType, taggableId),
        method: 'PUT',
        body: { tagIds },
      }),
      invalidatesTags: (_r, _e, { taggableType, taggableId }) => [
        { type: 'EntityTags', id: `${taggableType}:${taggableId}` },
        // Refresh the entity row so the tag chip column updates.
        { type: taggableType, id: taggableId },
        { type: taggableType, id: 'LIST' },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetTagsForEntityQuery,
  useLazyGetTagsForEntityQuery,
  useSetTagsForEntityMutation,
} = taggableApi;

export default taggableApi;
